import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import TeamManagementPanel from '../components/Admin/TeamManagement/TeamManagementPanel';
import UserManagementPanel from '../components/Admin/UserManagementPanel';
import { HistoricalUsagePanel } from '../components/ClientDetails/HistoricalUsagePanel';
import { LedgerTab } from '../components/ClientDetails/LedgerTab';
import { OverviewTab } from '../components/ClientDetails/OverviewTab';
import { SettingsTab } from '../components/ClientDetails/SettingsTab';
import { usePricing } from '../contexts/PricingContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { type ClientDetailsFormData, clientDetailsSchema } from '../schemas/clientDetailsSchema';
import {
  getClientData,
  setManualSubscription,
  updateClientProfile,
} from '../services/adminService';
import {
  type BillingLedger,
  subscribeToBillingLedger,
  topUpBillingLedger,
  updateBillingLedger,
} from '../services/billingService';
import type { Company } from '../types/company';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Define specific limits interface based on existing usage
interface SubscriptionLimits {
  numUsers: number;
  numTeams: number;
  numTenders: number;
  numDrafts: number;
  numReviews: number;
  numReports: number;
  numDraftsPerTender: number;
  numReviewsPerTender: number;
  numReportsPerTender: number;
  numAdHocDocs: number;
  numAdHocDocsPerTender: number;
  numSectionChats: number;
  numSectionChatsPerTender: number;
  [key: string]: number; // Allow for dynamic keys
}

interface SubscriptionCredits {
  numTenders: number;
  numDrafts: number;
  numReviews: number;
  numReports: number;
  numAdHocDocs: number;
  numSectionChats: number;
  [key: string]: number;
}

interface FirestoreTimestampLike {
  toDate: () => Date;
}

const isFirestoreTimestamp = (value: unknown): value is FirestoreTimestampLike => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as FirestoreTimestampLike).toDate === 'function'
  );
};

interface SecondsTimestamp {
  seconds: number;
}

const hasSeconds = (value: unknown): value is SecondsTimestamp => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'seconds' in value &&
    typeof (value as SecondsTimestamp).seconds === 'number'
  );
};

const defaultLimits: SubscriptionLimits = {
  numUsers: 1, // Minimum 1 (Admin)
  numTeams: 1,
  numTenders: 0,
  numDrafts: 0,
  numReviews: 0,
  numReports: 0,
  numDraftsPerTender: 0,
  numReviewsPerTender: 0,
  numReportsPerTender: 0,
  numAdHocDocs: 0,
  numAdHocDocsPerTender: 0,
  numSectionChats: 0,
  numSectionChatsPerTender: 0,
};

const defaultCredits: SubscriptionCredits = {
  numTenders: 0,
  numDrafts: 0,
  numReviews: 0,
  numReports: 0,
  numAdHocDocs: 0,
  numSectionChats: 0,
};

const ClientDetailsPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [client, setClient] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ClientDetailsFormData>({
    resolver: zodResolver(clientDetailsSchema),
    defaultValues: {
      clientName: '',
      primaryContactEmail: '',
      logo: '',
      billingCurrency: 'AUD',
      hasMatchmakingAccess: false,
      enableConneqtAccess: false,
      isIsolatedInstance: false,
      enableProcurementModule: false,
      enableBiddingModule: true,
      isDemo: false,
      limits: { numUsers: 1, numTeams: 1 },
      subscriptionStatus: 'active',
      managedBy: 'stripe',
      currentPeriodEnd: '',
    },
  });

  const managedBy = watch('managedBy');
  const isStripeManaged = managedBy === 'stripe';

  // Need credits to be stored to save back, but not in the form
  const [credits, setCredits] = useState<SubscriptionCredits>(defaultCredits);
  // Full limits also need to be preserved
  const [fullLimits, setFullLimits] = useState<SubscriptionLimits>(defaultLimits);

  // Ledger State
  const [ledger, setLedger] = useState<BillingLedger | null>(null);
  const [topUpAmount, setTopUpAmount] = useState<string>('0');
  const [isUpdatingLedger, setIsUpdatingLedger] = useState(false);

  const { pricingSettings } = usePricing();

  const fetchClient = useCallback(async () => {
    if (!clientId) {
      return;
    }
    setLoading(true);
    try {
      const clientData = await getClientData(clientId);

      if (clientData) {
        setClient(clientData);

        let currentPeriodEndStr = '';
        const rawPeriodEnd = clientData.subscription?.current_period_end;
        if (rawPeriodEnd) {
          if (isFirestoreTimestamp(rawPeriodEnd)) {
            currentPeriodEndStr = rawPeriodEnd.toDate().toISOString().substring(0, 10);
          } else if (rawPeriodEnd instanceof Date) {
            currentPeriodEndStr = rawPeriodEnd.toISOString().substring(0, 10);
          } else if (hasSeconds(rawPeriodEnd)) {
            currentPeriodEndStr = new Date(rawPeriodEnd.seconds * 1000)
              .toISOString()
              .substring(0, 10);
          } else if (typeof rawPeriodEnd === 'string') {
            const d = new Date(rawPeriodEnd);
            if (!Number.isNaN(d.getTime())) {
              currentPeriodEndStr = d.toISOString().substring(0, 10);
            }
          }
        }

        const limitsData = {
          ...defaultLimits,
          ...clientData.subscription?.limits,
          numTeams:
            clientData.subscription?.limits?.numTeams ??
            (clientData as unknown as { maxTeams?: number }).maxTeams ??
            defaultLimits.numTeams,
        } as SubscriptionLimits;

        setFullLimits(limitsData);

        if (clientData.subscription?.credits) {
          setCredits({
            ...defaultCredits,
            ...clientData.subscription.credits,
          } as SubscriptionCredits);
        } else {
          setCredits(defaultCredits);
        }

        reset({
          clientName: clientData.clientName || clientData.name || '',
          primaryContactEmail: clientData.primaryContactEmail || clientData.email || '',
          logo: clientData.logo || '',

          billingCurrency:
            ((clientData as unknown as Record<string, unknown>).billingCurrency as string) || 'AUD',
          hasMatchmakingAccess: clientData.hasMatchmakingAccess || false,
          enableConneqtAccess: clientData.enableConneqtAccess || false,
          isIsolatedInstance: clientData.clientSettings?.isIsolatedInstance || false,
          enableProcurementModule: Boolean(clientData.clientSettings?.enableProcurementModule),
          enableBiddingModule: clientData.clientSettings?.enableBiddingModule !== false,
          isDemo: clientData.isDemo || false,
          limits: {
            numUsers: limitsData.numUsers,
            numTeams: limitsData.numTeams,
          },
          subscriptionStatus: clientData.subscription?.status || 'active',
          managedBy:
            clientData.subscription?.managedBy ||
            (clientData.billingType === 'self-managed' ? 'stripe' : 'manual'),
          currentPeriodEnd: currentPeriodEndStr,
        });
      } else {
        showSnackbar('Client not found', 'error');
        navigate('/admin?tab=clients');
      }
    } catch {
      showSnackbar('Failed to fetch client details', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId, navigate, showSnackbar, reset]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  useEffect(() => {
    if (!clientId) {
      return;
    }

    const unsubscribe = subscribeToBillingLedger(clientId, (data) => {
      setLedger(data);
    });

    return () => unsubscribe();
  }, [clientId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleTopUp = async () => {
    const parsedAmount = parseFloat(topUpAmount) || 0;
    if (!clientId || parsedAmount === 0) {
      return;
    }
    setIsUpdatingLedger(true);
    try {
      const currentBalance = ledger?.balance || 0;
      await topUpBillingLedger(clientId, parsedAmount, currentBalance, 'admin_manual');

      showSnackbar(`Successfully added ${parsedAmount} credits.`, 'success');
      setTopUpAmount('0');
    } catch (_err) {
      showSnackbar('Failed to top up ledger.', 'error');
    } finally {
      setIsUpdatingLedger(false);
    }
  };

  const handleTogglePAYG = async (newVal: boolean) => {
    if (!clientId) {
      return;
    }
    setIsUpdatingLedger(true);
    try {
      await updateBillingLedger(clientId, { isPAYG: newVal });
      showSnackbar(`Usage billing ${newVal ? 'enabled' : 'disabled'}.`, 'success');
    } catch (_err) {
      showSnackbar('Failed to toggle Usage billing.', 'error');
    } finally {
      setIsUpdatingLedger(false);
    }
  };

  const handleUpdateDiscount = async (newDiscount: number) => {
    if (!clientId) {
      return;
    }
    setIsUpdatingLedger(true);
    try {
      const clampedDiscount = Math.max(0, Math.min(100, newDiscount));
      await updateBillingLedger(clientId, { discount: clampedDiscount });
      showSnackbar(`Discount updated to ${clampedDiscount}%.`, 'success');
    } catch (_err) {
      showSnackbar('Failed to update discount.', 'error');
    } finally {
      setIsUpdatingLedger(false);
    }
  };

  const onSave = async (data: ClientDetailsFormData) => {
    if (!client || !clientId) {
      return;
    }
    setSaving(true);
    try {
      await updateClientProfile(clientId, {
        clientName: data.clientName,
        primaryContactEmail: data.primaryContactEmail,
        logo: data.logo,
        billingCurrency: data.billingCurrency,
        numTeams: data.limits.numTeams,
        hasMatchmakingAccess: data.hasMatchmakingAccess,
        enableConneqtAccess: data.enableConneqtAccess,
        isDemo: data.isDemo,
        clientSettings: {
          ...client.clientSettings,
          isIsolatedInstance: data.isIsolatedInstance,
          enableProcurementModule: data.enableProcurementModule,
          enableBiddingModule: data.enableBiddingModule,
        },
      });

      let formattedPeriodEnd: string | undefined;
      if (data.currentPeriodEnd) {
        formattedPeriodEnd = new Date(data.currentPeriodEnd).toISOString();
      }

      const updatedSubscription = {
        ...client.subscription,
        status: data.subscriptionStatus,
        managedBy: data.managedBy as 'stripe' | 'manual',
        ...(formattedPeriodEnd !== undefined && { current_period_end: formattedPeriodEnd }),
        limits: {
          ...fullLimits,
          numUsers: data.limits.numUsers,
          numTeams: data.limits.numTeams,
        },
        credits: {
          ...credits,
        },
      };

      await setManualSubscription(clientId, updatedSubscription);

      setClient((prev) =>
        prev
          ? {
              ...prev,
              clientName: data.clientName,
              primaryContactEmail: data.primaryContactEmail,
              logo: data.logo,
              billingCurrency: data.billingCurrency,
              subscription: updatedSubscription,
            }
          : null
      );

      showSnackbar('Client updated successfully', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      showSnackbar(`Failed to update client: ${message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer maxWidth={false}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (!client || !clientId) {
    return null;
  }

  return (
    <PageContainer title="Client Details" maxWidth={false}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSave)}
        sx={{ width: '100%', p: { xs: 2, md: 3 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/admin?tab=clients')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {client.clientName || client.name || 'Unnamed Client'}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button variant="contained" color="primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        <Paper sx={{ mb: 3, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="client details tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Overview" />
              <Tab label="Settings" />
              <Tab label="Teams" />
              <Tab label="Users" />
              <Tab label="Ledger" />
            </Tabs>
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <CustomTabPanel value={tabValue} index={0}>
              <OverviewTab control={control} errors={errors} isStripeManaged={isStripeManaged} />

              <HistoricalUsagePanel client={client} />
            </CustomTabPanel>

            <CustomTabPanel value={tabValue} index={1}>
              <SettingsTab control={control} errors={errors} isStripeManaged={isStripeManaged} />
            </CustomTabPanel>

            <CustomTabPanel value={tabValue} index={2}>
              <TeamManagementPanel
                clientId={clientId || ''}
                maxTeams={watch('limits.numTeams') || 1}
              />
            </CustomTabPanel>

            <CustomTabPanel value={tabValue} index={3}>
              <UserManagementPanel clientId={clientId || ''} />
            </CustomTabPanel>

            <CustomTabPanel value={tabValue} index={4}>
              <LedgerTab
                clientId={clientId}
                client={client}
                ledger={ledger}
                pricingSettings={pricingSettings}
                isUpdatingLedger={isUpdatingLedger}
                topUpAmount={topUpAmount}
                setTopUpAmount={setTopUpAmount}
                handleTopUp={handleTopUp}
                handleTogglePAYG={handleTogglePAYG}
                handleUpdateDiscount={handleUpdateDiscount}
                subscriptionStatus={watch('subscriptionStatus')}
              />
            </CustomTabPanel>
          </Box>
        </Paper>
      </Box>
    </PageContainer>
  );
};

export default ClientDetailsPage;
